import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Calculator,
  Users,
  Sparkles,
  Bed,
  Car,
  Utensils,
  ShieldCheck,
  ChefHat,
  Instagram,
  Youtube,
  ArrowRight,
  Menu as MenuIcon,
  X,
} from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_padmanag-tools/artifacts/yi18ch1w_PADMANAG__1_-removebg-preview.png";

const IMG = {
  mandap:
    "https://customer-assets.emergentagent.com/job_padmanag-tools/artifacts/d4hph30d_WhatsApp%20Image%202025-12-11%20at%205.45.58%20PM.jpeg",
  entrance:
    "https://customer-assets.emergentagent.com/job_padmanag-tools/artifacts/ld8b94wr_WhatsApp%20Image%202025-12-11%20at%205.45.59%20PM%20%281%29.jpeg",
  stage:
    "https://customer-assets.emergentagent.com/job_padmanag-tools/artifacts/psnv9h6v_WhatsApp%20Image%202025-12-03%20at%2015.46.22.jpeg",
  hall:
    "https://customer-assets.emergentagent.com/job_padmanag-tools/artifacts/dmm4amm6_WhatsApp%20Image%202025-12-11%20at%205.46.05%20PM.jpeg",
};

const WHATSAPP = "https://wa.me/919845726257";
const PHONE = "+919845726257";
const EMAIL = "padmanagcoventionhall@gmail.com";

const SPACES = [
  {
    key: "one-day",
    title: "1 Day Event",
    capacity: "1000 floating · 400 seating",
    blurb:
      "Floating capacity for 1,000 guests with 400 comfortable seating arrangements. Includes 1 bride room, 1 bridegroom room, and 2 stage-side rooms. Kitchen catering for up to 1,500 members.",
    points: [
      "Bride & Bridegroom rooms",
      "2 stage-side rooms",
      "Stainless steel dining setup",
      "Light instrumental music",
    ],
    image: IMG.mandap,
  },
  {
    key: "two-day",
    title: "2 Day Event",
    capacity: "1000 floating · Extended stay",
    blurb:
      "Everything in the 1-day package plus 2 store rooms in the dining hall and 2 non-A/C rooms accommodating up to 6 people each. Dedicated cleaning staff of 6 across the event days.",
    points: [
      "2 store rooms in dining hall",
      "2 additional non-A/C rooms (6 pax each)",
      "Cleaning crew of 6 members",
      "On-site plumber & electrician",
    ],
    image: IMG.stage,
  },
  {
    key: "small",
    title: "Small Event Venue",
    capacity: "500 floating · 150 seating",
    blurb:
      "Intimate gatherings in a spacious A/C venue. Perfect for engagements, birthdays, corporate meets and smaller receptions. Security cameras and dedicated on-site staff included.",
    points: [
      "Fully A/C dining hall",
      "Stainless steel dining furniture",
      "On-site cleaning staff",
      "Plumber & electrician on call",
    ],
    image: IMG.hall,
  },
];

const AMENITIES = [
  { icon: Users, title: "1,000 guests", sub: "Floating capacity" },
  { icon: Bed, title: "20 A/C rooms", sub: "Incl. suite with lake view" },
  { icon: Car, title: "30+ car parking", sub: "Spacious on-site" },
  { icon: ChefHat, title: "Kitchen for 1,500", sub: "3 commercial gas cylinders" },
  { icon: ShieldCheck, title: "Security cameras", sub: "Throughout the venue" },
  { icon: Sparkles, title: "Centrally A/C", sub: "Premium ambience" },
  { icon: Utensils, title: "Stainless steel dining", sub: "Tables, chairs & tava" },
  { icon: MessageCircle, title: "Dedicated staff", sub: "Cleaning, plumber, electrician" },
];

const NAV = [
  { href: "#about", label: "About" },
  { href: "#spaces", label: "Spaces" },
  { href: "#gallery", label: "Gallery" },
  { href: "#calculators", label: "Calculators" },
  { href: "#contact", label: "Contact" },
];

export default function Landing() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="pm-site" data-testid="pm-landing">
      {/* NAV */}
      <nav className={`pm-nav ${scrolled ? "pm-nav-scrolled" : ""}`} data-testid="pm-nav">
        <a href="#top" className="pm-nav-brand">
          <img src={LOGO} alt="Padmanag" className="pm-nav-logo" />
          <span className="pm-nav-name">Padmanag</span>
        </a>

        <ul className="pm-nav-links">
          {NAV.map((n) => (
            <li key={n.href}>
              <a href={n.href}>{n.label}</a>
            </li>
          ))}
        </ul>

        <a className="pm-nav-cta" href={`tel:${PHONE}`} data-testid="pm-nav-call">
          <Phone size={14} strokeWidth={2} />
          <span>+91 98457 26257</span>
        </a>

        <button
          className="pm-nav-burger"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="pm-nav-burger"
        >
          {open ? <X size={22} /> : <MenuIcon size={22} />}
        </button>

        {open && (
          <div className="pm-nav-mobile" data-testid="pm-nav-mobile">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)}>
                {n.label}
              </a>
            ))}
            <a href={`tel:${PHONE}`} className="pm-nav-mobile-call">
              <Phone size={14} /> Call us
            </a>
          </div>
        )}
      </nav>

      {/* HERO */}
      <header id="top" className="pm-hero" data-testid="pm-hero">
        <div
          className="pm-hero-bg"
          style={{ backgroundImage: `url(${IMG.mandap})` }}
        />
        <div className="pm-hero-veil" />
        <div className="pm-hero-inner">
          <img src={LOGO} alt="Padmanag logo" className="pm-hero-logo" />
          <div className="pm-hero-eyebrow">Mallathahalli · Bengaluru</div>
          <h1 className="pm-hero-h1">
            Where <span className="pm-italic">celebrations</span> come alive
          </h1>
          <p className="pm-hero-sub">
            A premier centrally A/C convention hall with a floating capacity of 1,000 guests,
            20 luxurious A/C rooms, and a suite with a lake view — designed for weddings,
            receptions and corporate gatherings.
          </p>

          <div className="pm-hero-ctas">
            <a
              className="pm-btn pm-btn-primary"
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pm-hero-book"
            >
              Book Your Event
              <ArrowRight size={16} strokeWidth={2} />
            </a>
            <a className="pm-btn pm-btn-ghost" href="#calculators" data-testid="pm-hero-calc">
              <Calculator size={16} strokeWidth={1.8} />
              Catering Calculators
            </a>
          </div>

          <div className="pm-hero-meta">
            <div>
              <div className="pm-meta-num">1,000</div>
              <div className="pm-meta-key">Floating guests</div>
            </div>
            <div>
              <div className="pm-meta-num">20</div>
              <div className="pm-meta-key">A/C rooms</div>
            </div>
            <div>
              <div className="pm-meta-num">30+</div>
              <div className="pm-meta-key">Car parking</div>
            </div>
            <div>
              <div className="pm-meta-num">1,500</div>
              <div className="pm-meta-key">Kitchen capacity</div>
            </div>
          </div>
        </div>
      </header>

      {/* ABOUT */}
      <section id="about" className="pm-section pm-about" data-testid="pm-about">
        <div className="pm-about-grid">
          <div className="pm-about-imgs">
            <div className="pm-about-img-1" style={{ backgroundImage: `url(${IMG.entrance})` }} />
            <div className="pm-about-img-2" style={{ backgroundImage: `url(${IMG.hall})` }} />
          </div>
          <div className="pm-about-text">
            <div className="pm-eyebrow">About our venue</div>
            <h2 className="pm-h2">
              The <span className="pm-italic">finest</span> wedding & convention hall in
              Mallathahalli
            </h2>
            <p>
              Padmanag Convention Hall, located in Mallathahalli, Bengaluru, is a fully
              air-conditioned venue with a floating capacity of <strong>1,000 guests</strong>,
              <strong> 400 seating arrangements</strong>, and <strong>220 dining seats</strong>.
              It features a fully equipped kitchen, luxurious room accommodations, and parking
              for over 30 cars.
            </p>
            <p>
              Whether you're hosting a grand wedding, an intimate reception or a corporate event,
              our team and decorators in Bangalore deliver memorable experiences at an affordable
              price. Guests can also enjoy <strong>20 luxurious A/C rooms</strong>, including a
              suite with a stunning lake view.
            </p>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="pm-text-link"
              data-testid="pm-about-cta"
            >
              Talk to us on WhatsApp <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SPACES */}
      <section id="spaces" className="pm-section pm-spaces" data-testid="pm-spaces">
        <div className="pm-section-head">
          <div className="pm-eyebrow">Our spaces</div>
          <h2 className="pm-h2">
            Three ways to <span className="pm-italic">celebrate</span>
          </h2>
        </div>

        <div className="pm-spaces-grid">
          {SPACES.map((s) => (
            <article key={s.key} className="pm-space-card" data-testid={`pm-space-${s.key}`}>
              <div
                className="pm-space-img"
                style={{ backgroundImage: `url(${s.image})` }}
              />
              <div className="pm-space-body">
                <h3 className="pm-space-title">{s.title}</h3>
                <div className="pm-space-cap">{s.capacity}</div>
                <p className="pm-space-blurb">{s.blurb}</p>
                <ul className="pm-space-points">
                  {s.points.map((p) => (
                    <li key={p}>
                      <span className="pm-dot" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* AMENITIES */}
      <section className="pm-section pm-amenities" data-testid="pm-amenities">
        <div className="pm-section-head pm-section-head-center">
          <div className="pm-eyebrow pm-eyebrow-light">What we offer</div>
          <h2 className="pm-h2 pm-h2-light">
            Built for <span className="pm-italic">grand</span> hospitality
          </h2>
        </div>

        <div className="pm-amenities-grid">
          {AMENITIES.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.title} className="pm-amenity">
                <div className="pm-amenity-icon">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <div className="pm-amenity-title">{a.title}</div>
                <div className="pm-amenity-sub">{a.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="pm-section pm-gallery" data-testid="pm-gallery">
        <div className="pm-section-head">
          <div className="pm-eyebrow">Gallery</div>
          <h2 className="pm-h2">
            Moments at <span className="pm-italic">Padmanag</span>
          </h2>
        </div>

        <div className="pm-gallery-grid">
          <div className="pm-g pm-g-1" style={{ backgroundImage: `url(${IMG.mandap})` }} />
          <div className="pm-g pm-g-2" style={{ backgroundImage: `url(${IMG.entrance})` }} />
          <div className="pm-g pm-g-3" style={{ backgroundImage: `url(${IMG.stage})` }} />
          <div className="pm-g pm-g-4" style={{ backgroundImage: `url(${IMG.hall})` }} />
        </div>
      </section>

      {/* CALCULATORS */}
      <section id="calculators" className="pm-section pm-calcs" data-testid="pm-calcs">
        <div className="pm-section-head">
          <div className="pm-eyebrow">Plan your budget</div>
          <h2 className="pm-h2">
            Live <span className="pm-italic">catering</span> calculators
          </h2>
          <p className="pm-section-lead">
            Pick a venue, slide to your guest count and see all-inclusive pricing across our four
            signature menu packages — Silver, Gold, Platinum and Diamond.
          </p>
        </div>

        <div className="pm-calcs-grid">
          <Link to="/convention" className="pm-calc-card" data-testid="pm-calc-convention">
            <div className="pm-calc-icon">
              <Sparkles size={20} strokeWidth={1.7} />
            </div>
            <div className="pm-calc-label">Convention Hall</div>
            <div className="pm-calc-title">300 – 1,000 guests</div>
            <p className="pm-calc-blurb">
              Grand weddings, receptions and corporate events. Hall + menu, all-inclusive.
            </p>
            <span className="pm-calc-cta">
              Open calculator <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/party" className="pm-calc-card" data-testid="pm-calc-party">
            <div className="pm-calc-icon">
              <Utensils size={20} strokeWidth={1.7} />
            </div>
            <div className="pm-calc-label">Party Hall</div>
            <div className="pm-calc-title">50 – 500 guests</div>
            <p className="pm-calc-blurb">
              Intimate events with tiered slab pricing — engagements, birthdays, small parties.
            </p>
            <span className="pm-calc-cta">
              Open calculator <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="pm-section pm-contact" data-testid="pm-contact">
        <div className="pm-contact-grid">
          <div>
            <div className="pm-eyebrow">Get in touch</div>
            <h2 className="pm-h2">
              Visit us in <span className="pm-italic">person</span>
            </h2>
            <p className="pm-contact-lead">
              We love our customers — feel free to visit our convention hall during business
              hours, or reach out anytime by phone or WhatsApp.
            </p>

            <ul className="pm-contact-list">
              <li>
                <MapPin size={18} strokeWidth={1.6} />
                <div>
                  <div className="pm-contact-key">Address</div>
                  <div className="pm-contact-val">
                    No. 8/2-25-1, 60 Feet Road, NGK & M.P.M Layout, Near Mallathahalli Lake,
                    9th Block, Bengaluru — 560056
                  </div>
                </div>
              </li>
              <li>
                <a href={`tel:${PHONE}`} data-testid="pm-contact-phone">
                  <Phone size={18} strokeWidth={1.6} />
                  <div>
                    <div className="pm-contact-key">Phone</div>
                    <div className="pm-contact-val">+91 98457 26257</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pm-contact-whatsapp"
                >
                  <MessageCircle size={18} strokeWidth={1.6} />
                  <div>
                    <div className="pm-contact-key">WhatsApp</div>
                    <div className="pm-contact-val">Message us instantly</div>
                  </div>
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} data-testid="pm-contact-email">
                  <Mail size={18} strokeWidth={1.6} />
                  <div>
                    <div className="pm-contact-key">Email</div>
                    <div className="pm-contact-val">{EMAIL}</div>
                  </div>
                </a>
              </li>
              <li>
                <Clock size={18} strokeWidth={1.6} />
                <div>
                  <div className="pm-contact-key">Hours</div>
                  <div className="pm-contact-val">Open today · 9:00 AM – 5:00 PM</div>
                </div>
              </li>
            </ul>

            <div className="pm-contact-socials">
              <a
                href="https://www.instagram.com/padmanagconventionhall"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="pm-social-ig"
              >
                <Instagram size={18} strokeWidth={1.6} />
              </a>
              <a
                href="https://www.youtube.com/channel/UC8O0qaloyz8hVz2hS05wCkw"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                data-testid="pm-social-yt"
              >
                <Youtube size={18} strokeWidth={1.6} />
              </a>
            </div>
          </div>

          <div className="pm-map" data-testid="pm-map">
            <iframe
              title="Padmanag Convention Hall location"
              src="https://www.google.com/maps?q=Padmanag+Convention+Hall+Mallathahalli+Bengaluru&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pm-footer" data-testid="pm-footer">
        <div className="pm-footer-inner">
          <div className="pm-footer-brand">
            <img src={LOGO} alt="Padmanag" />
            <div>
              <div className="pm-footer-name">PADMANAG</div>
              <div className="pm-footer-sub">Convention Hall · Mallathahalli, Bengaluru</div>
            </div>
          </div>

          <div className="pm-footer-cols">
            <div>
              <div className="pm-footer-col-title">Explore</div>
              <a href="#about">About</a>
              <a href="#spaces">Spaces</a>
              <a href="#gallery">Gallery</a>
            </div>
            <div>
              <div className="pm-footer-col-title">Plan</div>
              <Link to="/convention">Convention Calculator</Link>
              <Link to="/party">Party Hall Calculator</Link>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                Enquire on WhatsApp
              </a>
            </div>
            <div>
              <div className="pm-footer-col-title">Reach us</div>
              <a href={`tel:${PHONE}`}>+91 98457 26257</a>
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              <span className="pm-footer-addr">
                60 Feet Road, NGK & M.P.M Layout, Mallathahalli, Bengaluru — 560056
              </span>
            </div>
          </div>
        </div>

        <div className="pm-footer-bottom">
          <span>© {new Date().getFullYear()} Padmanag Convention Hall · All rights reserved</span>
        </div>
      </footer>
    </div>
  );
}
